import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAddThought } from "../hooks/queries";

export function QuickThoughtFAB() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [defer, setDefer] = useState<"" | "today" | "tomorrow" | "evening" | "weekly">("");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const add = useAddThought();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        setMenuOpen(false);
      } else if (e.key === "Escape") {
        setOpen(false);
        setMenuOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function submit() {
    if (!content.trim()) return;
    await add.mutateAsync({ content: content.trim(), deferTo: defer || undefined });
    setContent(""); setDefer("");
    setOpen(false);
  }

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error("[shukan] FAB fullscreen toggle failed:", err);
    }
  };

  return (
    <div ref={containerRef} className="shukan-fab-container">
      <div className="shukan-fab-menu">
        <button
          type="button"
          className={`shukan-fab-item ${menuOpen ? "show" : ""}`}
          onClick={() => {
            setOpen(true);
            setMenuOpen(false);
          }}
          title="Quick Thought"
        >
          <span>✍️</span>
          <span className="shukan-fab-item-label">Quick Thought</span>
        </button>

        <button
          type="button"
          className={`shukan-fab-item ${menuOpen ? "show" : ""}`}
          onClick={() => {
            window.dispatchEvent(new CustomEvent("shukan-trigger-create-habit"));
            setMenuOpen(false);
          }}
          title="Create Habit / Task"
        >
          <span>📝</span>
          <span className="shukan-fab-item-label">Add Habit/Task</span>
        </button>

        <button
          type="button"
          className={`shukan-fab-item ${menuOpen ? "show" : ""}`}
          onClick={() => {
            toggleFullscreen();
            setMenuOpen(false);
          }}
          title="Toggle Fullscreen"
        >
          <span>🌐</span>
          <span className="shukan-fab-item-label">Toggle Fullscreen</span>
        </button>

        <button
          type="button"
          className={`shukan-fab-item ${menuOpen ? "show" : ""}`}
          onClick={() => {
            navigate("/chat");
            setMenuOpen(false);
          }}
          title="Chat with AI"
        >
          <span>💬</span>
          <span className="shukan-fab-item-label">Ask AI</span>
        </button>

        <button
          type="button"
          className={`shukan-fab-item ${menuOpen ? "show" : ""}`}
          onClick={() => {
            navigate("/calendar");
            setMenuOpen(false);
          }}
          title="Calendar"
        >
          <span>📅</span>
          <span className="shukan-fab-item-label">Calendar</span>
        </button>
      </div>

      <button
        type="button"
        className={`shukan-fab ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle actions menu"
        title="Toggle actions menu"
      >
        +
      </button>

      {open ? (
        <div
          role="dialog"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 110, padding: "1rem",
            backdropFilter: "blur(2px)",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "4px",
              padding: "1rem", maxWidth: "32rem", width: "100%",
              boxShadow: "0 4px 24px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{ marginBottom: "0.5rem", color: "var(--accent)", fontWeight: "bold" }}>$ quick thought</div>
            <textarea
              ref={inputRef}
              className="shukan-input"
              style={{ minHeight: "5rem" }}
              placeholder="...fleeting idea, reminder, todo"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
              }}
            />
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", alignItems: "center" }}>
              <select
                className="shukan-input"
                style={{ maxWidth: "9rem" }}
                value={defer}
                onChange={(e) => setDefer(e.target.value as typeof defer)}
              >
                <option value="">no defer</option>
                <option value="today">today</option>
                <option value="tomorrow">tomorrow</option>
                <option value="evening">evening</option>
                <option value="weekly">weekly</option>
              </select>
              <button type="button" className="shukan-btn shukan-btn-primary" onClick={submit} disabled={add.isPending || !content.trim()}>
                {add.isPending ? "..." : "$ save (⌘⏎)"}
              </button>
              <button type="button" className="shukan-btn" onClick={() => setOpen(false)}>cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
